import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, Action, ActionCreator } from 'redux';
import * as _ from 'lodash';
import { ISearchAppState } from '../index';
import { Pagination } from './pagination/pagination';
import {
    ISearchResultDTO,
    ISearchRequestDTO,
    ITrackClick,
    ITermDTO,
    IAutocompleteResults,
    DefaultAutoCompleteResults,
    FILE_EXTENSION_TERM,
    ISearchResultHitDTO
} from '../DTO/search';
import { IFetchAction } from '../middleware/fetch-middleware';
import { SearchError } from './error/error';
import { DropdownSelect, IDropdownOption } from './inputs/dropdown';
import { SearchBar } from './searching/search-bar';
import { Facets } from './filters/facets';
import { SortGroup } from './filters/sort';
import { ResultList } from './results/result-list';
import { getEventListFeaturedEvent, fetchAutoCompleteResults, getEventListResults, getSearchResults, trackClick } from '../ducks/search-api';
import { getContentProps } from '../ducks/content-api';
import { ResultCount } from './results/result-count';
import { IContentProps } from '../models/content-props';
import { ISort, searchSortModel, GetSorts, IGlobalSort, ISortModelId } from '../models/sorts';
import { AnalyticsCategories, EventName, TriggerAnalyticsEventFor } from '../models/analytics';
import { ChapterLogo } from './events/chapter-logo';

interface IStateProps {
    contentProps: IContentProps,
    searchResults: ISearchResultDTO,
}

interface IDispatchProps {
    getContentProps: () => IFetchAction;
    getSearchResults: (criteria: ISearchRequestDTO) => IFetchAction;
}

interface IOwnProps {
    contentId: string;
    dateSortRelevanceFilter: number;
    setQueryParams: (params: { [key: string]: string[] }) => void
    initialParams: { [key: string]: string[] },
    isEventList: boolean,
    chapterLogoUrl: string,
    chapterHomeUrl: string
}

type IProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    searchText: string;
    pageSize: number;
    pageNumber: number;
    isLoading: boolean;
    hasSearched: boolean;
    selectedTerms: ITermDTO[];
    context: string;
    autoCompleteResults: IAutocompleteResults;
    sortsModel: ISort[];
    fetchError: boolean;
    toggleMobileFilters: boolean;
    isArchived: boolean;
}

export function Search({
    isEventList = false,
    chapterLogoUrl = "",
    chapterHomeUrl = "",
    contentId,
    contentProps = {} as IContentProps,
    searchResults = DefaultAutoCompleteResults as any,
    initialParams,
    getContentProps,
    getSearchResults: getSearchResultsProp,
    dateSortRelevanceFilter,
    setQueryParams
}: IProps) {
    
    const [toggleMobileFilters, setToggleMobileFilters] = React.useState(true);
    const updateSort = (sortId: string): ((sort: ISort) => ISort) => {        
        if (!sortId) {            
            sortId = isEventList ? ISortModelId.newest : ISortModelId.relevance;
        }

        return function (sort: ISort) {
            sort.selected = sort.id === sortId;
            return sort;
        }
    };    

    const getUpdatedSorts = (sortId: string, onPageLoad = false) => {
        const componentState: Partial<IState> = state ? state : { sortsModel: [] };
        const sortsModel = onPageLoad ? searchSortModel : componentState.sortsModel;
        return sortsModel.map(updateSort(sortId));
    };

    const getInitialStateFromQueryParams = () => {
        const searchText = initialParams.q && customDecodeURIComponent(initialParams.q[0]) || '';
        delete initialParams.q;

        const pageSize = initialParams.pageSize && + initialParams.pageSize[0] || 10;
        delete initialParams.pageSize;

        const pageNumber = initialParams.pageNumber && + initialParams.pageNumber[0] || 1;
        delete initialParams.pageNumber;

        const selectedTerms = initialParams.selectedTerms ? initialParams.selectedTerms.map(t => JSON.parse(customDecodeURIComponent(t))) : [];
        delete initialParams.selectedTerms;

        const context = initialParams.context && customDecodeURIComponent(initialParams.context[0]) || '';
        delete initialParams.context;

        const sortsModel = getUpdatedSorts(initialParams.sortId && initialParams.sortId[0] || '', true);

        return {
            searchText,
            pageSize,
            pageNumber,
            selectedTerms,
            context,
            sortsModel,
            isLoading: false,
            hasSearched: false,
            autoCompleteResults: {
                hits: [],
                isActive: false,
                activeHit: null,
            },
            fetchError: false,
            toggleMobileFilters: false,
            isArchived: false
        };
    };

    const customDecodeURIComponent = (encodedURI: string): string => {
        return decodeURIComponent(encodedURI.replace(/\+/g, ' '));
    }

    const [state, setState] = React.useState<IState>(getInitialStateFromQueryParams);
    const [eventListFeaturedEvents, setEventListFeaturedEvents] = React.useState<ISearchResultHitDTO[]>([]);

    // componentDidMount
    React.useEffect(() => {
        getContentProps();
        getSearchResults();
        setToggleMobileFilters(false);
    }, []);

    const onResultCLick = (hitId: string) => {
        trackClick({
            hitId: hitId,
            searchTerm: state.searchText,
            trackingId: searchResults.trackingId
        });
    };

    const getSelectedFacets = () => {
        let facetMap = new Map<string, ITermDTO[]>();
        [].forEach.call(state.selectedTerms, (term: ITermDTO) => {
            term.selected = true;
            if (facetMap.has(term.fieldName)) {
                facetMap.get(term.fieldName).push(term);
            }
            else {
                facetMap.set(term.fieldName, [term]);
            }
        });

        return Array.from(facetMap.keys()).map(key => Object.freeze({ fieldName: key, terms: facetMap.get(key) }));;
    };

    const getSelectedSort = (): ISort => state.sortsModel.filter((x: ISort) => x.selected)[0];

    // Effect for getSearchResults
    React.useEffect(() => {
        // Get just the selected facets and selected terms
        const selectedSort = getSelectedSort();
        async function fetchSearchResultsProp() {
            try {
                const { fetch } = getSearchResultsProp({
                    searchTerm: state.searchText,
                    pageSize: contentProps.pageSize,
                    pageNumber: state.pageNumber,
                    facets: getSelectedFacets(),
                    context: state.context,
                    sortField: selectedSort.sortField,
                    sortDirection: selectedSort.direction,
                    dateSortRelevanceFilter: dateSortRelevanceFilter,
                    includeSuggestion: true,
                    eventListPageId: parseInt(contentId),
                    IsArchiveList: state.isArchived
                } as ISearchRequestDTO);
                                              
                if(!isEventList){
                    await fetch
                }
                else{
                    const eventListFeaturedEvents = getEventListFeaturedEvent(parseInt(contentId))
                    const [ firstRequest, secondRequest ] = await Promise.all([fetch, eventListFeaturedEvents])

                    setEventListFeaturedEvents(secondRequest);
                }
                setState(prevState => ({
                    ...prevState,
                    isLoading: false,
                    fetchError: false,
                })           
                );                
            } catch {
                setState(prevState => ({
                    ...prevState,
                    fetchError: true
                }));
            }
        }

        fetchSearchResultsProp();
    }, [state.hasSearched]);

    const getSearchResults = () => {
        setState(prevState => ({
            ...prevState,
            isLoading: true,
            hasSearched: !prevState.hasSearched
        }));
    };

    // Effect for onSearch
    React.useEffect(() => {
        setAutoCompleteActive(false);
    }, [state.searchText]);

    // Effect for...
    React.useEffect(() => {
        onUpdateQuery();
        getSearchResults();
    }, [
            state.searchText, // onSearch
            state.context, // setContext
            state.sortsModel, // onSortSelect
            state.selectedTerms, // setFacetTerm and resetFacets
            state.pageNumber // setPageNumber
        ]);

    const onSearch = (searchText: string, isArchived: boolean) => {
        setState(prevState => ({
            ...prevState,
            pageNumber: 1,
            selectedTerms: [],
            searchText,
            isArchived
        }));
    };

    const setContext = (context: string) => {
        if (context !== null && context !== undefined) {
            triggerContextChangedEvent(context, state.context);
        }

        setState(prevState => ({
            ...prevState,
            context,
            pageNumber: 1,
            selectedTerms: []
        }));
    };

    const onInputChange = (searchText: string) => {
        getAutoCompleteResults(searchText);
    };

    const onSortSelect = (sortId: string) => {
        const updatedSorts = getUpdatedSorts(sortId);

        setState(prevState => ({
            ...prevState,
            sortsModel: updatedSorts,
            pageNumber: 1
        }));
    };

    const setAutoCompleteResults = (results: IAutocompleteResults) => {
        setState(prevState => ({
            ...prevState,
            autoCompleteResults: results
        }));
    };

    const setAutoCompleteActive = (value: boolean) => {
        const results = { ...state.autoCompleteResults }
        results.isActive = value;
        results.activeHit = -1;
        setAutoCompleteResults(results);
    };

    const setAutoCompleteActiveHit = (index: number) => {
        const results = { ...state.autoCompleteResults }
        results.activeHit = index;
        setAutoCompleteResults(results);
    };

    const getAutoCompleteResults = (searchText: string) => {
        if (searchText.length > 2) {
            fetchAutoCompleteResults(searchText)
                .then((data) => {
                    const results = { ...DefaultAutoCompleteResults }

                    if (data) {
                        results.activeHit = -1;
                        results.hits = data.hits;
                        results.isActive = true;
                    }
                    setAutoCompleteResults(results);
                })

            setAutoCompleteActive(true);
        }
    };

    const setFacetTerm = (id: number) => {
        setState(prevState => {
            const selectedTermIds = prevState.selectedTerms.map(term => term.id);
            let selectedTerms = prevState.selectedTerms;
            const index = selectedTermIds.indexOf(id);
            // if exist, deselect the term
            if (index >= 0) {
                // use spread and slice so that useEffect hook's change detection works
                selectedTerms = [
                    ...selectedTerms.slice(0, index),
                    ...selectedTerms.slice(index + 1, selectedTerms.length)
                ];
            }
            // if not exist, add the term to the selected terms
            else {
                const allTerms = searchResults.facets.reduce((terms, facet) => [...terms, ...facet.terms], []);
                const newlySelectedTerm = allTerms.filter(t => t.id == id)[0];
                // use spread so that useEffect hook's change detection works
                selectedTerms = [...selectedTerms, newlySelectedTerm];

                const facet = newlySelectedTerm;
                const value = facet.selected ? "false" : "true";
                TriggerAnalyticsEventFor(EventName.FacetToggledEvent, AnalyticsCategories.Search, facet.displayName, value);
            }

            return {
                ...prevState,
                selectedTerms,
                pageNumber: 1
            };
        });
    };

    const resetFacets = () => {
        // Clear all doesn't effect things that are in the file extension group
        setState(prevState => {
            const selectedTerms = prevState.selectedTerms
                .filter(term => term.fieldName === FILE_EXTENSION_TERM);
            return {
                ...prevState,
                selectedTerms,
                pageNumber: 1
            };
        });
    };

    // NOTE: setSearchText is a not used method, but the below is its conversion anyway.
    //       However, its related useEffect will be a partial duplication of
    //       the useEffect with state.searchText as dependency above.
    //       Therefore, it is commented out.
    //       If setSearchText is to be used, the useEffect block below should be removed.
    // React.useEffect(() => {
    //     onUpdateQuery();
    // }, [state.searchText]);
    // const setSearchText = (searchText: string) => {
    //     setState(prevState => ({ ...prevState, searchText }));
    // };

    const setPageNumber = (pageNumber: number) => {
        TriggerAnalyticsEventFor(EventName.PageClickedEvent, AnalyticsCategories.Search, pageNumber, state.pageNumber);

        setState(prevState => ({
            ...prevState,
            pageNumber
        }));

        // Scroll to the top of the search app
        const searchApp = document.querySelector('.adage-search');

        if (searchApp) {
            // Small delay to ensure rendering is complete
            setTimeout(() => {
                let stickyHeaderHeight = 12;
            
                if (window.innerWidth > 1320) {
                    // Find the sticky header element
                    const stickyHeader = document.querySelector('.header') as HTMLElement;
                    stickyHeaderHeight = stickyHeader ? stickyHeader.offsetHeight + 12 : 0;
                }
                
                // Calculate position accounting for the sticky header
                const searchAppPosition = searchApp.getBoundingClientRect().top + window.scrollY;
                
                // Scroll to the element with an offset for the header
                window.scrollTo({
                    top: searchAppPosition - stickyHeaderHeight,
                    behavior: 'smooth'
                });
            }, 100);
        } else {
            // Small delay to ensure rendering is complete
            setTimeout(() => {
                // Fallback to scrolling to top if search app not found
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });  
            }, 100);
        }
    };

    const onUpdateQuery = () => {
        const query: { [key: string]: string[] } = {};

        if (state.searchText.length > 0) {
            query.q = [state.searchText];
        }
        if (state.pageNumber) {
            query.pageNumber = [state.pageNumber.toString()];
        }
        if (state.context) {
            query.context = [state.context];
        }
        if (state.selectedTerms) {
            query.selectedTerms = state.selectedTerms.map((term: ITermDTO) =>
                encodeURIComponent(
                    JSON.stringify(
                        Object.freeze({
                            id: term.id,
                            fieldName: term.fieldName,
                            term: term.term
                        })
                    )
                )
            );
        }

        if (state.sortsModel) {
            query.sortId = [getSelectedSort().id];
        }

        // Update the url
        setQueryParams(query);
    };

    const triggerContextChangedEvent = (label: string, value: string) => {
        if (label === '') {
            label = 'All';
        }

        if (value === '') {
            value = 'All';
        }

        TriggerAnalyticsEventFor(EventName.ContextChangedEvent, AnalyticsCategories.Search, label, value);
    }

    const setErrorState = (value: boolean) => {
        setState(prevState => ({
            ...prevState,
            fetchError: value
        }));
    };

    const [searchText, setSearchText] = React.useState(state.searchText);
    const [isArchive, setIsArchive] = React.useState(false);

    const handleUpcomingEventsButtonClick = () => {
        if (isArchive) {
            setIsArchive(false);
            onSearch(searchText, false);
        }
    };

    const handlePastEventsButtonClick = () => {
        if (!isArchive) {
            setIsArchive(true);
            onSearch(searchText, true);   
        }
    };    
    return (
        <div className={`adage-search-container ${isEventList && 'event-list-page'}`}>
            <h1 className="visually-hidden">Search Results</h1>
            <div className="adage-search-container-inner">
                <div className="adage-search-sidebar">
                    <div className="adage-search-mobile-only">
                        <SearchBar
                            id='site-search-form'
                            searchText={state.searchText}
                            isEventList={isEventList}
                            onSearch={onSearch}
                            onChange={onInputChange}
                            searchButtonText={contentProps.searchButtonText}
                            autoCompleteResults={state.autoCompleteResults}
                            setActiveHit={setAutoCompleteActiveHit}
                            setAutoCompleteActive={setAutoCompleteActive}
                            searchSuggestionText={contentProps.searchSuggestionText}
                            placeHolder={contentProps.searchSuggestionText}
                            searchSuggestion={searchResults.suggestion}
                            loading={state.isLoading}
                            isArchive={isArchive}
                            contextComponent={<DropdownSelect
                                onDropdownSelect={setContext}
                                placeHolder={contentProps.contextPlaceholderText}
                                selectedId={state.context}
                                values={contentProps.contextItems ? contentProps.contextItems.map(x => {
                                    return Object.freeze({
                                        id: x,
                                        display: x
                                    }) as IDropdownOption
                                }) : []}  
                            />}
                        />
                    </div>
                    <ChapterLogo isEventPage={isEventList} logoUrl={chapterLogoUrl} chapterHomeUrl={ chapterHomeUrl } />
                    {!isEventList && (
                        <div className="adage-search-mobile-only">
                            <ResultCount
                                content={contentProps}
                                currentPage={state.pageNumber}
                                totalResults={searchResults.totalResults}
                                isEventList={isEventList}
                                pageSize={contentProps.pageSize}
                                currentPageResults={searchResults.results ? searchResults.results.length : 0}
                                isLoading={state.isLoading}
                            />
                        </div>
                    )}
                    <div className="adage-search-sidebar-options">
                        <div className="adage-search-mobile-only adage-search-filters-mobile">
                            <button className={`un-button adage-search-filters-toggle ${toggleMobileFilters ? "open" : "closed"}`}
                                onClick={() => setToggleMobileFilters(!toggleMobileFilters)}>
                                {isEventList ? 'Filter' : 'Toggle Filters'}
                            </button>
                            {toggleMobileFilters &&
                                <Facets
                                    facets={searchResults.facets}
                                    selectedTermIds={state.selectedTerms ? state.selectedTerms.map(term => term.id) : []}
                                    onTermClick={setFacetTerm}
                                    onResetClick={resetFacets}
                                    contentProps={contentProps}
                                    isEventList={isEventList} />
                            }
                        </div>
                        <div className="adage-search-desktop-only">
                            <Facets
                                facets={searchResults.facets}
                                selectedTermIds={state.selectedTerms ? state.selectedTerms.map(term => term.id) : []}
                                onTermClick={setFacetTerm}
                                onResetClick={resetFacets}
                                contentProps={contentProps}
                                isEventList={isEventList} />
                        </div>
                        {!isEventList && (
                            <div className="adage-search-sorts">
                                <SortGroup
                                    sortGroupName='sorts'
                                    sorts={GetSorts(state.sortsModel, contentProps)}
                                    onSort={(sort: IGlobalSort<ISearchResultDTO>) => onSortSelect(sort.sortId)}
                                    sortLabel={contentProps.sortsLabel}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="adage-search-content">
                    {state.fetchError &&
                        <SearchError
                            errorText={contentProps.generalErrorText}
                            onDismiss={() => setErrorState(false)}
                        />
                    }
                    <div className="adage-search-desktop-only">
                        <SearchBar
                            id='site-search-form'
                            searchText={state.searchText}
                            isEventList={isEventList}
                            onSearch={onSearch}
                            onChange={onInputChange}
                            searchButtonText={contentProps.searchButtonText}
                            autoCompleteResults={state.autoCompleteResults}
                            setActiveHit={setAutoCompleteActiveHit}
                            setAutoCompleteActive={setAutoCompleteActive}
                            searchSuggestionText={contentProps.searchSuggestionText}
                            placeHolder={contentProps.searchSuggestionText}
                            searchSuggestion={searchResults.suggestion}
                            loading={state.isLoading}
                            isArchive={isArchive}
                            contextComponent={<DropdownSelect
                                onDropdownSelect={setContext}
                                placeHolder={contentProps.contextPlaceholderText}
                                selectedId={state.context}
                                values={contentProps.contextItems ? contentProps.contextItems.map(x => {
                                    return Object.freeze({
                                        id: x,
                                        display: x
                                    }) as IDropdownOption
                                }) : []}
                            />}
                        />
                    </div>

                    <div className="adage-search-desktop-only">
                        <ResultCount
                            content={contentProps}
                            currentPage={state.pageNumber}
                            totalResults={searchResults.totalResults}
                            isEventList={isEventList}
                            pageSize={contentProps.pageSize}
                            currentPageResults={searchResults.results ? searchResults.results.length : 0}
                            isLoading={state.isLoading}
                        />
                    </div>                                        

                    {isEventList && 
                        <div className='events-buttons-container'>
                            <button className={isArchive ? "btn-tertiary-inverted" : "btn-tertiary"} type="submit" onClick={handleUpcomingEventsButtonClick}>
                                Upcoming Events
                            </button>

                            <button className={isArchive ? "btn-tertiary" : "btn-tertiary-inverted"} type="submit" onClick={handlePastEventsButtonClick}>
                                Past Events
                            </button>
                        </div>           
                    }                    

                    <ResultList
                        contentProps={contentProps}
                        isEventList={isEventList}
                        results={searchResults.results}
                        isArchived={state.isArchived}
                        eventListFeaturedEvents={eventListFeaturedEvents}
                        onResultClick={onResultCLick}
                        isLoading={state.isLoading} />

                    {(isEventList == true && 
                        state.isLoading == false && 
                        (searchResults.totalResults == 0 || (searchResults.totalResults <= contentProps.pageSize * (state.pageNumber - 1))) &&
                        (eventListFeaturedEvents == null || eventListFeaturedEvents.length == 0))
                        && 
                        <p className="adage-search-results-number" dangerouslySetInnerHTML={{ __html: contentProps.zeroResultsText }}>                            
                        </p>
                    }
                </div>
            </div>
            <Pagination
                totalItems={searchResults.totalResults}
                pageSize={contentProps.pageSize}
                pageNumber={state.pageNumber}
                onPageSelect={(pageNumber) => setPageNumber(pageNumber)} />           
        </div>
    );
};

const mapStateToProps = (state: ISearchAppState): IStateProps => ({
    contentProps: state.contentState.props,        
    searchResults: state.searchResults.results    
});

const mapDispatchToProps = (dispatch: Dispatch<Action>, ownProps: IOwnProps): IDispatchProps => bindActionCreators({
    getContentProps: (() => getContentProps(ownProps.contentId)) as ActionCreator<IFetchAction>,
    getSearchResults: (ownProps.isEventList ? getEventListResults : getSearchResults) as ActionCreator<IFetchAction>
}, dispatch);

export const SearchApp = connect<IStateProps, IDispatchProps, IOwnProps>(mapStateToProps, mapDispatchToProps)(Search);
